import React from 'react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Heading, Text, Spinner, Alert, AlertIcon, ChakraProvider, extendTheme, Center,
  TableContainer, Table, Thead, Tbody, Tr, Th, Td, Button, Tag, VStack, HStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure,
  Link, Tooltip, ButtonGroup, IconButton, useToast, CheckboxGroup, Checkbox, Stack
} from '@chakra-ui/react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useAuthCliente } from '../context/AuthClienteContext';
import { useReservas } from '../hooks/useReservas';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale/es';
import { format, isBefore, startOfToday, startOfWeek, endOfWeek, addDays, subDays, addMonths, subMonths, addWeeks, subWeeks, isToday as isTodayDateFns, startOfDay, endOfDay } from 'date-fns';
import { FaWhatsapp } from 'react-icons/fa';
import LoadingAnimation from '../components/LoadingAnimation';
import WeekView from '../components/WeekView';
import MonthView from '../components/MonthView';
import Lottie from 'lottie-react';
import animationData from '../assets/loadingAnimation.json';
import { collection, onSnapshot, getFirestore } from 'firebase/firestore';

registerLocale('es', es);

export default function PublicReservasPage() {
  const { companyId } = useParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('day');
  const { settings, bookings, companyData, loading, error, refetchReservas } = useReservas(companyId, selectedDate, view);
  
  // ✅ Nuevo estado para los eventos globales
  const [globalEvents, setGlobalEvents] = useState([]);
  const db = getFirestore();

  // ✅ useEffect para cargar los eventos globales de la empresa
  useEffect(() => {
    if (!companyId) return;
    const eventsRef = collection(db, 'companies', companyId, 'events');
    const unsub = onSnapshot(eventsRef, (snapshot) => {
      setGlobalEvents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, [companyId, db]);

  const dynamicTheme = useMemo(() => {
    const theme = companyData?.theme || {};
    return extendTheme({
      styles: { global: { 'body': { bg: theme.background || 'gray.50' }}},
      colors: { brand: { 500: theme.primary || '#3182CE' }},
    });
  }, [companyData]);

  if (loading && !settings) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <Center h="100vh"><Alert status="error" variant="subtle"><AlertIcon />{error}</Alert></Center>;
  }

  return (
    <ChakraProvider theme={dynamicTheme}>
      <PageContent 
        settings={settings}
        bookings={bookings}
        globalEvents={globalEvents} // ✅ Pasamos los eventos a PageContent
        loading={loading}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        contactPhone={companyData?.contactPhone}
        view={view}
        setView={setView}
        companyId={companyId}
        refetchReservas={refetchReservas}
        companyData={companyData}
      />
    </ChakraProvider>
  );
}

const DatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
  <IconButton size="sm" variant="ghost" aria-label="Seleccionar fecha" icon={<CalendarIcon w={5} h={5} />} onClick={onClick} ref={ref} />
));

function PageContent({ settings, bookings, globalEvents, loading, selectedDate, setSelectedDate, contactPhone, view, setView, companyId, refetchReservas, companyData }) {
  const { currentUser, createBooking } = useAuthCliente();
  const toast = useToast();
  const { isOpen: isGuestModalOpen, onOpen: onGuestModalOpen, onClose: onGuestModalClose } = useDisclosure();
  const { isOpen: isBookingModalOpen, onOpen: onBookingModalOpen, onClose: onBookingModalClose } = useDisclosure();
  
  const [isBooking, setIsBooking] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsToBook, setSlotsToBook] = useState([]);
  const [bookingObstacleMessage, setBookingObstacleMessage] = useState('');

  const handleSlotClick = useCallback((resource, timeSlot, day) => {
    const dateToBook = day || selectedDate;
    if (!currentUser) {
      onGuestModalOpen();
      return;
    }
    const dayOfWeek = dateToBook.getDay();
    const formattedDate = format(dateToBook, 'yyyy-MM-dd');
    const allTimeSlots = [];
    const { openingHour = 7, closingHour = 22 } = settings;
    for (let i = openingHour; i < closingHour; i++) {
        allTimeSlots.push({ start: `${String(i).padStart(2, '0')}:00` });
    }
    const startIndex = allTimeSlots.findIndex(ts => ts.start === timeSlot.start);
    const consecutiveSlots = [];
    for (let i = 0; i < 3; i++) {
      const currentIndex = startIndex + i;
      if (currentIndex >= allTimeSlots.length) break;
      const currentSlot = allTimeSlots[currentIndex];
      
      const isBooked = bookings.some(b => b.date === formattedDate && b.resourceId === resource.id && b.startTime === currentSlot.start);
      
      // ✅ Verificación de eventos globales y especiales
      const specialEvent = (resource.specialEvents || []).find(e => {
        const eventStart = startOfDay(new Date(e.startDate));
        const eventEnd = endOfDay(new Date(e.endDate));
        return startOfDay(dateToBook) >= eventStart && startOfDay(dateToBook) <= eventEnd;
      });
      const isSpecialEventBlocked = specialEvent && (specialEvent.type === 'vacation' || (specialEvent.startTime <= currentSlot.start && specialEvent.endTime > currentSlot.start));
      
      const globalEvent = (globalEvents || []).find(e => 
        (e.assignedResourceIds || []).includes(resource.id) && 
        (e.days || []).includes(dayOfWeek) && 
        currentSlot.start >= e.startTime && 
        currentSlot.start < e.endTime
      );

      if (!isBooked && !isSpecialEventBlocked && !globalEvent) {
        consecutiveSlots.push({
          resource: resource,
          date: formattedDate,
          startTime: currentSlot.start,
          endTime: `${String(parseInt(currentSlot.start) + 1).padStart(2, '0')}:00`
        });
      } else {
        break;
      }
    }
    const totalHoursInDay = closingHour - openingHour;
    if (consecutiveSlots.length < 3 && consecutiveSlots.length < totalHoursInDay - startIndex) {
      setBookingObstacleMessage("No puedes seleccionar más horas porque el siguiente horario está ocupado.");
    } else {
      setBookingObstacleMessage("");
    }
    setAvailableSlots(consecutiveSlots);
    setSlotsToBook(consecutiveSlots.length > 0 ? [consecutiveSlots[0].startTime] : []);
    onBookingModalOpen();
  }, [selectedDate, currentUser, onGuestModalOpen, settings, bookings, globalEvents, onBookingModalOpen]);

  const handleConfirmBooking = async () => {
    const selectedBookingSlots = availableSlots.filter(slot => slotsToBook.includes(slot.startTime));
    if (selectedBookingSlots.length === 0) {
      toast({ title: "Debes seleccionar al menos un horario.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (!currentUser || currentUser.bookingTokens < selectedBookingSlots.length) {
      toast({ title: "No tienes suficientes tokens.", status: "error", duration: 3000, isClosable: true });
      return;
    }
    setIsBooking(true);
    try {
      await createBooking({ companyId, slotsToBook: selectedBookingSlots });
      toast({ title: "¡Reserva confirmada!", description: `Has reservado ${selectedBookingSlots.length} hora(s).`, status: "success", duration: 5000, isClosable: true });
      onBookingModalClose();
      if (refetchReservas) await refetchReservas();
    } catch (error) {
      toast({ title: "Error al reservar", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setIsBooking(false);
      setSlotsToBook([]);
    }
  };

  const DayView = useMemo(() => {
    if (!settings?.resources) return null;
    const now = new Date();
    const currentHour = now.getHours();
    const isToday = isTodayDateFns(selectedDate);
    const isPastDate = isBefore(selectedDate, startOfToday());
    const timeSlots = [];
    const { openingHour = 7, closingHour = 22 } = settings;
    for (let i = openingHour; i < closingHour; i++) {
        const start = `${String(i).padStart(2, '0')}:00`;
        const end = `${String(i + 1).padStart(2, '0')}:00`;
        timeSlots.push({ start, end });
    }
    const selectedDay = selectedDate.getDay();
    const reservationGrid = timeSlots.map(timeSlot => {
      const row = { time: timeSlot, slots: [] };
      settings.resources.forEach(resource => {
        const { schedule = {}, specialEvents = [] } = resource;
        let status = 'unavailable';
        let statusData = null;
        const slotStartTime = new Date(`1970-01-01T${timeSlot.start}`);
        const resourceStartTime = new Date(`1970-01-01T${schedule.startTime || '23:59'}`);
        const resourceEndTime = new Date(`1970-01-01T${schedule.endTime || '00:00'}`);
        
        if ((schedule.operatingDays || []).includes(selectedDay) && slotStartTime >= resourceStartTime && slotStartTime < resourceEndTime) {
          status = 'available';
          
          const specialEvent = (specialEvents || []).find(e => {
            const eventStart = startOfDay(new Date(e.startDate));
            const eventEnd = endOfDay(new Date(e.endDate));
            return startOfDay(selectedDate) >= eventStart && startOfDay(selectedDate) <= eventEnd;
          });
          if (specialEvent) {
            if (specialEvent.type === 'vacation' || (specialEvent.startTime <= timeSlot.start && specialEvent.endTime > timeSlot.start)) {
              status = 'event';
              statusData = specialEvent;
            }
          }
          
          if (status === 'available') {
            const globalEvent = (globalEvents || []).find(e => 
              (e.assignedResourceIds || []).includes(resource.id) && 
              (e.days || []).includes(selectedDay) && 
              timeSlot.start >= e.startTime && 
              timeSlot.start < e.endTime
            );
            if (globalEvent) {
              status = 'clinic';
              statusData = globalEvent;
            }
          }

          if (status === 'available') {
            const booking = bookings.find(b => b.date === format(selectedDate, 'yyyy-MM-dd') && b.resourceId === resource.id && b.startTime === timeSlot.start);
            if(booking) {
              status = 'booked';
              statusData = booking;
            }
          }
        }
        row.slots.push({ resource, status, statusData });
      });
      return row;
    });

    return (
      <TableContainer borderWidth="1px" borderRadius="lg" bg="white" mt={6}>
        <Table variant="simple">
          <Thead bg="gray.50"><Tr><Th w={{ base: '100px', md: '140px' }}>Horario</Th>{settings.resources.map(resource => (<Th key={resource.id} isTruncated>{resource.name}</Th>))}</Tr></Thead>
          <Tbody>
            {reservationGrid.map(({ time, slots }) => (
              <Tr key={time.start}><Td fontWeight="bold">{time.start} - {time.end}</Td>
                {slots.map(({ resource, status, statusData }, index) => {
                  const slotHour = parseInt(time.start, 10);
                  const isPastSlot = isPastDate || (isToday && slotHour < currentHour);
                  return (
                  <Td key={`${resource.id}-${index}`} textAlign="center" px={1} bg={status === 'unavailable' ? 'gray.50' : 'white'}>
                    {(() => {
                      if (status === 'booked') {
                        if (currentUser && statusData.userId === currentUser.uid) { return <Tag colorScheme="blue">Reservado por ti</Tag>; }
                        return <Tag colorScheme="red">Reservado</Tag>;
                      }
                      if (status === 'clinic') { return <Tooltip label={statusData.title}><Tag colorScheme="purple">CLÍNICA</Tag></Tooltip>; }
                      if (status === 'event') { return <Tooltip label={statusData.title}><Tag bg={statusData.color || 'orange.400'} color="white">EVENTO</Tag></Tooltip>; }
                      if (status === 'available') { return <Button size="sm" colorScheme="green" variant="outline" onClick={() => handleSlotClick(resource, time, null)} isDisabled={isPastSlot}>Disponible</Button>; }
                      return null;
                    })()}
                  </Td>
                );})}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );
  }, [settings, bookings, globalEvents, selectedDate, currentUser, handleSlotClick]);

  const dynamicHeader = useMemo(() => {
    if (!selectedDate || !(selectedDate instanceof Date) || isNaN(selectedDate)) return 'Cargando fecha...';
    if (view === 'month') return format(selectedDate, 'MMMM yyyy', { locale: es });
    if (view === 'week') {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
      if (weekStart.getMonth() !== weekEnd.getMonth()) return `${format(weekStart, 'd MMM', { locale: es })} - ${format(weekEnd, 'd MMM, yyyy', { locale: es })}`;
      return `${format(weekStart, 'd')} - ${format(weekEnd, 'd MMMM, yyyy', { locale: es })}`;
    }
    return format(selectedDate, 'EEEE, d \'de\' MMMM \'de\' yyyy', { locale: es });
  }, [selectedDate, view]);

  const renderAgenda = () => {
    if (view === 'day') return DayView;
    if (view === 'week') return <WeekView settings={settings} bookings={bookings} globalEvents={globalEvents} selectedDate={selectedDate} onSlotSelect={handleSlotClick} />;
    if (view === 'month') return <MonthView settings={settings} bookings={bookings} globalEvents={globalEvents} selectedDate={selectedDate} onDateClick={(day) => { setSelectedDate(day); setView('day'); }} />;
    return null;
  };
  
  const handleDateChange = (increment) => {
    let newDate;
    if (view === 'day') newDate = increment > 0 ? addDays(selectedDate, 1) : subDays(selectedDate, 1);
    else if (view === 'week') newDate = increment > 0 ? addWeeks(selectedDate, 1) : subWeeks(selectedDate, 1);
    else newDate = increment > 0 ? addMonths(selectedDate, 1) : subMonths(selectedDate, 1);
    setSelectedDate(newDate);
  };
  
  const handleGoToToday = () => {
    setSelectedDate(new Date());
    setView('day');
  };

  return (
    <>
      <Modal isOpen={isGuestModalOpen} onClose={onGuestModalClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader color="brand.500">¡Empieza a Reservar Hoy Mismo!</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Vaya, parece que aún no tienes una cuenta en nuestro sistema.</Text>
            <Text mt={4} fontWeight="bold">Comunícate con el administrador del club para que puedas crear una cuenta y reserves cuando quieras.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onGuestModalClose}>Entendido</Button>
            {contactPhone && (<Link href={`https://wa.me/${contactPhone.replace(/\D/g, '')}`} isExternal><Button bg="#25D366" color="white" _hover={{ bg: '#1DA851' }} leftIcon={<FaWhatsapp />}>Comunicarme ahora</Button></Link>)}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBookingModalOpen} onClose={onBookingModalClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Confirmar Reserva</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {currentUser && availableSlots.length > 0 ? (
              <VStack spacing={4} align="stretch">
                <Text>Selecciona los horarios que deseas reservar para <strong>{availableSlots[0].resource.name}</strong>:</Text>
                <CheckboxGroup colorScheme="brand" value={slotsToBook} onChange={setSlotsToBook}>
                  <Stack spacing={2}>
                    {availableSlots.map((slot, index) => (
                      <Checkbox key={slot.startTime} value={slot.startTime} isDisabled={index > 0 && !slotsToBook.includes(availableSlots[index - 1].startTime)}>
                        {slot.startTime} - {slot.endTime}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
                <Text mt={4} fontWeight="bold">
                  Total a consumir: {slotsToBook.length} token(s).
                  Te quedarán: {currentUser.bookingTokens - slotsToBook.length}.
                </Text>
                {bookingObstacleMessage && slotsToBook.length === availableSlots.length && (
                  <Alert status="info" mt={2} borderRadius="md"><AlertIcon />{bookingObstacleMessage}</Alert>
                )}
              </VStack>
            ) : (<Alert status="info"><AlertIcon />Parece que no hay horarios disponibles consecutivos.</Alert>)}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onBookingModalClose}>Cancelar</Button>
            <Button colorScheme="brand" onClick={handleConfirmBooking} isLoading={isBooking} isDisabled={!currentUser || currentUser.bookingTokens < slotsToBook.length || slotsToBook.length === 0}>
              Confirmar ({slotsToBook.length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box p={{ base: 2, md: 4 }}>
        <VStack spacing={5} w="full">
          <VStack spacing={0} p={3} bg="white" borderRadius="md" boxShadow="sm" w="fit-content" mx="auto">
            <Text fontSize="sm" color="gray.500">Estás viendo la agenda para:</Text>
            <Heading size="md" color="brand.500">{companyData?.name || 'Cargando club...'}</Heading>
          </VStack>
          <Button onClick={handleGoToToday} size="sm" variant="solid" colorScheme='blue'>Ir a Hoy</Button>
          <HStack spacing={1}>
              <IconButton size="sm" variant="ghost" aria-label="Anterior" icon={<ChevronLeftIcon w={6} h={6} />} onClick={() => handleDateChange(-1)} />
              <Heading as="h2" size={{base: 'md', md: 'lg'}} color="black" textTransform="capitalize" whiteSpace="nowrap">{dynamicHeader}</Heading>
              <IconButton size="sm" variant="ghost" aria-label="Siguiente" icon={<ChevronRightIcon w={6} h={6} />} onClick={() => handleDateChange(1)} />
          </HStack>
          <DatePicker selected={selectedDate} onChange={(date) => setSelectedDate(date)} customInput={<DatePickerInput />} withPortal locale="es" />
          <ButtonGroup isAttached size="sm">
            <Button onClick={() => setView('day')} variant={view === 'day' ? 'solid' : 'outline'} colorScheme="brand">Día</Button>
            <Button onClick={() => setView('week')} variant={view === 'week' ? 'solid' : 'outline'} colorScheme="brand">Semana</Button>
            <Button onClick={() => setView('month')} variant={view === 'month' ? 'solid' : 'outline'} colorScheme="brand">Mes</Button>
          </ButtonGroup>
        </VStack>
        {loading ? (<Center p={10}><Lottie animationData={animationData} loop={true} style={{width: 150, height: 150}} /></Center>) : (renderAgenda())}
      </Box>
    </>
  );
}